import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import styled from 'styled-components';
import GlobalStyles from './styles/GlobalStyles';
import Header from './components/Header';
import Footer from './components/Footer';
import ErrorBoundary from './components/ErrorBoundary';
import AdminRoutes from './admin/routes/AdminRoutes';
import { loadCdnConfig } from './utils/cdn';
import DashboardPage from './admin/pages/DashboardPage';
import PrivateRoute from './components/PrivateRoute';

const ChatWidget = lazy(() => import('./components/ChatWidget'));
const Home = lazy(() => import('./pages/Home'));
const OpenClaw = lazy(() => import('./pages/OpenClaw'));
const Solutions = lazy(() => import('./pages/Solutions'));
const SolutionDetail = lazy(() => import('./pages/SolutionDetail'));
const Products = lazy(() => import('./pages/Products'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const ServiceSupport = lazy(() => import('./pages/ServiceSupport'));
const Contact = lazy(() => import('./pages/Contact'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Profile = lazy(() => import('./pages/Profile'));
const BindPhone = lazy(() => import('./pages/BindPhone'));
const WechatCallbackTest = lazy(() => import('./pages/WechatCallbackTest'));
const WechatConfirmLogin = lazy(() => import('./pages/WechatConfirmLogin'));
const AIDetailPage = lazy(() => import('./pages/AIDetailPage'));
const VoiceDetailPage = lazy(() => import('./pages/VoiceDetailPage'));
const ImageDetailPage = lazy(() => import('./pages/ImageDetailPage'));
const DataDetailPage = lazy(() => import('./pages/DataDetailPage'));
const RecommendationDetailPage = lazy(() => import('./pages/RecommendationDetailPage'));
const CallCenterDetailPage = lazy(() => import('./pages/CallCenterDetailPage'));
const UnifiedCommunicationDetailPage = lazy(() => import('./pages/UnifiedCommunicationDetailPage'));
const DispatchSystemDetailPage = lazy(() => import('./pages/DispatchSystemDetailPage'));
const VideoConferenceDetailPage = lazy(() => import('./pages/VideoConferenceDetailPage'));
const LiveStreamingDetailPage = lazy(() => import('./pages/LiveStreamingDetailPage'));
const AIVoiceRobotDetailPage = lazy(() => import('./pages/AIVoiceRobotDetailPage'));
const VoiceLineDetailPage = lazy(() => import('./pages/VoiceLineDetailPage'));
const CloudSeatDetailPage = lazy(() => import('./pages/CloudSeatDetailPage'));
const SMSPortalDetailPage = lazy(() => import('./pages/SMSPortalDetailPage'));
const FreeswitchDetailPage = lazy(() => import('./pages/FreeswitchDetailPage'));
const WeakElectricDetailPage = lazy(() => import('./pages/WeakElectricDetailPage'));
const SystemIntegrationDetailPage = lazy(() => import('./pages/SystemIntegrationDetailPage'));
const CablingDetailPage = lazy(() => import('./pages/CablingDetailPage'));
const ServerRoomDetailPage = lazy(() => import('./pages/ServerRoomDetailPage'));
const NetworkSecurityDetailPage = lazy(() => import('./pages/NetworkSecurityDetailPage'));
const MaintenanceServiceDetailPage = lazy(() => import('./pages/MaintenanceServiceDetailPage'));
const CloudMigrationDetailPage = lazy(() => import('./pages/CloudMigrationDetailPage'));
const PublicOpinionServiceDetailPage = lazy(() => import('./pages/PublicOpinionServiceDetailPage'));
const SoftwareCustomizationDetailPage = lazy(() => import('./pages/SoftwareCustomizationDetailPage'));
const ITInfrastructurePlanningDetailPage = lazy(() => import('./pages/ITInfrastructurePlanningDetailPage'));
const ITInfrastructureAssessmentDetailPage = lazy(() => import('./pages/ITInfrastructureAssessmentDetailPage'));
const ITInfrastructureTransformationDetailPage = lazy(() => import('./pages/ITInfrastructureTransformationDetailPage'));
const SmartConstructionDetailPage = lazy(() => import('./pages/SmartConstructionDetailPage'));
const SmartAssessmentDetailPage = lazy(() => import('./pages/SmartAssessmentDetailPage'));
const SmartTransformationDetailPage = lazy(() => import('./pages/SmartTransformationDetailPage'));
const TenderProposalDetailPage = lazy(() => import('./pages/TenderProposalDetailPage'));
const ITConsultantDetailPage = lazy(() => import('./pages/ITConsultantDetailPage'));
const SmartMedicalDetailPage = lazy(() => import('./pages/SmartMedicalDetailPage'));
const HospitalInfoDetailPage = lazy(() => import('./pages/HospitalInfoDetailPage'));
const InternetHospitalDetailPage = lazy(() => import('./pages/InternetHospitalDetailPage'));
const SmartFactoryDetailPage = lazy(() => import('./pages/industry/SmartFactoryDetailPage'));
const SmartManufacturingDetailPage = lazy(() => import('./pages/industry/SmartManufacturingDetailPage'));
const IndustrialInternetDetailPage = lazy(() => import('./pages/industry/IndustrialInternetDetailPage'));
const SmartCampusDetailPage = lazy(() => import('./pages/industry/SmartCampusDetailPage'));
const OnlineEducationDetailPage = lazy(() => import('./pages/industry/OnlineEducationDetailPage'));
const TrainingManagementDetailPage = lazy(() => import('./pages/industry/TrainingManagementDetailPage'));
const SmartTransportDetailPage = lazy(() => import('./pages/industry/SmartTransportDetailPage'));
const LogisticsTransportDetailPage = lazy(() => import('./pages/industry/LogisticsTransportDetailPage'));
const PublicTransportDetailPage = lazy(() => import('./pages/industry/PublicTransportDetailPage'));
const SmartGovernmentDetailPage = lazy(() => import('./pages/industry/SmartGovernmentDetailPage'));
const PublicServiceDetailPage = lazy(() => import('./pages/industry/PublicServiceDetailPage'));
const EmergencyCommandDetailPage = lazy(() => import('./pages/industry/EmergencyCommandDetailPage'));
const SmartParkDetailPage = lazy(() => import('./pages/industry/SmartParkDetailPage'));
const ParkOperationDetailPage = lazy(() => import('./pages/industry/ParkOperationDetailPage'));
const ParkIntelligenceDetailPage = lazy(() => import('./pages/industry/ParkIntelligenceDetailPage'));
const SmartWarehouseDetailPage = lazy(() => import('./pages/industry/SmartWarehouseDetailPage'));
const WMSDetailPage = lazy(() => import('./pages/industry/WMSDetailPage'));
const LogisticsDistributionDetailPage = lazy(() => import('./pages/industry/LogisticsDistributionDetailPage'));
const ColdChainLogisticsDetailPage = lazy(() => import('./pages/industry/ColdChainLogisticsDetailPage'));
const SmartFinanceDetailPage = lazy(() => import('./pages/industry/SmartFinanceDetailPage'));
const DigitalBankDetailPage = lazy(() => import('./pages/industry/DigitalBankDetailPage'));
const SmartRetailDetailPage = lazy(() => import('./pages/industry/SmartRetailDetailPage'));
const ECommerceDetailPage = lazy(() => import('./pages/industry/ECommerceDetailPage'));
const SmartEnergyDetailPage = lazy(() => import('./pages/industry/SmartEnergyDetailPage'));
const SmartPowerGridDetailPage = lazy(() => import('./pages/industry/SmartPowerGridDetailPage'));
const SmartAgricultureDetailPage = lazy(() => import('./pages/industry/SmartAgricultureDetailPage'));
const SmartBreedingDetailPage = lazy(() => import('./pages/industry/SmartBreedingDetailPage'));
const LivestockManagementDetailPage = lazy(() => import('./pages/industry/LivestockManagementDetailPage'));
const ProductTraceabilityDetailPage = lazy(() => import('./pages/industry/ProductTraceabilityDetailPage'));
const SmartCityDetailPage = lazy(() => import('./pages/industry/SmartCityDetailPage'));
const SmartCommunityDetailPage = lazy(() => import('./pages/industry/SmartCommunityDetailPage'));
const SmartEnvironmentDetailPage = lazy(() => import('./pages/industry/SmartEnvironmentDetailPage'));
const WasteManagementDetailPage = lazy(() => import('./pages/industry/WasteManagementDetailPage'));
const SmartTourismDetailPage = lazy(() => import('./pages/industry/SmartTourismDetailPage'));
const HotelManagementDetailPage = lazy(() => import('./pages/industry/HotelManagementDetailPage'));
const SmartHotelDetailPage = lazy(() => import('./pages/industry/SmartHotelDetailPage'));
const RestaurantManagementDetailPage = lazy(() => import('./pages/industry/RestaurantManagementDetailPage'));
const OnlineOrderingDetailPage = lazy(() => import('./pages/industry/OnlineOrderingDetailPage'));
const FinancialInformationDetailPage = lazy(() => import('./pages/industry/FinancialInformationDetailPage'));
const InsuranceBusinessDetailPage = lazy(() => import('./pages/industry/InsuranceBusinessDetailPage'));
const InternetFinanceDetailPage = lazy(() => import('./pages/industry/InternetFinanceDetailPage'));
const SmartMediaDetailPage = lazy(() => import('./pages/industry/SmartMediaDetailPage'));
const NewMediaDetailPage = lazy(() => import('./pages/industry/NewMediaDetailPage'));

const AdminProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('adminToken');
  if (!token) {
    return <Navigate to="/admin/login" replace />;
  }
  return children;
};

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const MainContent = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const LoadingFallback = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  font-size: 1.125rem;
  color: #6b7280;
`;

function App() {
  // 启动时拉取后台「CDN 加速」配置（失败自动降级为源站）
  useEffect(() => {
    loadCdnConfig();
  }, []);

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <Router>
          <GlobalStyles />
          <AppContainer>
            {/* <SpringFestivalBanner /> */}
            <Header />
            <MainContent>
              <Suspense fallback={<LoadingFallback>加载中...</LoadingFallback>}>
              <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/openclaw" element={<OpenClaw />} />
              <Route path="/solutions" element={<Solutions />} />
              <Route path="/solutions/detail" element={<SolutionDetail />} />
              <Route path="/solutions/ai-detail" element={<AIDetailPage />} />
              <Route path="/solutions/voice-detail" element={<VoiceDetailPage />} />
              <Route path="/solutions/image-detail" element={<ImageDetailPage />} />
              <Route path="/solutions/data-detail" element={<DataDetailPage />} />
              <Route path="/solutions/recommendation-detail" element={<RecommendationDetailPage />} />
              <Route path="/solutions/callcenter-detail" element={<CallCenterDetailPage />} />
              <Route path="/solutions/unified-communication-detail" element={<UnifiedCommunicationDetailPage />} />
              <Route path="/solutions/dispatch-system-detail" element={<DispatchSystemDetailPage />} />
              <Route path="/solutions/video-conference-detail" element={<VideoConferenceDetailPage />} />
              <Route path="/solutions/live-streaming-detail" element={<LiveStreamingDetailPage />} />
              <Route path="/solutions/ai-voice-robot-detail" element={<AIVoiceRobotDetailPage />} />
              <Route path="/solutions/voice-line-detail" element={<VoiceLineDetailPage />} />
              <Route path="/solutions/cloud-seat-detail" element={<CloudSeatDetailPage />} />
              <Route path="/solutions/sms-portal-detail" element={<SMSPortalDetailPage />} />
              <Route path="/solutions/freeswitch-detail" element={<FreeswitchDetailPage />} />
              <Route path="/solutions/weak-electric-detail" element={<WeakElectricDetailPage />} />
              <Route path="/solutions/system-integration-detail" element={<SystemIntegrationDetailPage />} />
              <Route path="/solutions/cabling-detail" element={<CablingDetailPage />} />
              <Route path="/solutions/server-room-detail" element={<ServerRoomDetailPage />} />
              <Route path="/solutions/network-security-detail" element={<NetworkSecurityDetailPage />} />
              <Route path="/solutions/maintenance-service-detail" element={<MaintenanceServiceDetailPage />} />
              <Route path="/solutions/cloud-migration-detail" element={<CloudMigrationDetailPage />} />
              <Route path="/solutions/public-opinion-service-detail" element={<PublicOpinionServiceDetailPage />} />
              <Route path="/solutions/software-customization-detail" element={<SoftwareCustomizationDetailPage />} />
              <Route path="/solutions/it-infrastructure-planning-detail" element={<ITInfrastructurePlanningDetailPage />} />
              <Route path="/solutions/it-infrastructure-assessment-detail" element={<ITInfrastructureAssessmentDetailPage />} />
              <Route path="/solutions/it-infrastructure-transformation-detail" element={<ITInfrastructureTransformationDetailPage />} />
              <Route path="/solutions/smart-construction-detail" element={<SmartConstructionDetailPage />} />
              <Route path="/solutions/smart-assessment-detail" element={<SmartAssessmentDetailPage />} />
              <Route path="/solutions/smart-transformation-detail" element={<SmartTransformationDetailPage />} />
              <Route path="/solutions/tender-proposal-detail" element={<TenderProposalDetailPage />} />
              <Route path="/solutions/it-consultant-detail" element={<ITConsultantDetailPage />} />
              <Route path="/solutions/smart-medical-detail" element={<SmartMedicalDetailPage />} />
              <Route path="/solutions/hospital-info-detail" element={<HospitalInfoDetailPage />} />
              <Route path="/solutions/internet-hospital-detail" element={<InternetHospitalDetailPage />} />
              <Route path="/solutions/smart-factory-detail" element={<SmartFactoryDetailPage />} />
              <Route path="/solutions/smart-manufacturing-detail" element={<SmartManufacturingDetailPage />} />
              <Route path="/solutions/industrial-internet-detail" element={<IndustrialInternetDetailPage />} />
              <Route path="/solutions/smart-campus-detail" element={<SmartCampusDetailPage />} />
              <Route path="/solutions/online-education-detail" element={<OnlineEducationDetailPage />} />
              <Route path="/solutions/training-management-detail" element={<TrainingManagementDetailPage />} />
              <Route path="/solutions/smart-transport-detail" element={<SmartTransportDetailPage />} />
              <Route path="/solutions/logistics-transport-detail" element={<LogisticsTransportDetailPage />} />
              <Route path="/solutions/public-transport-detail" element={<PublicTransportDetailPage />} />
              <Route path="/solutions/smart-government-detail" element={<SmartGovernmentDetailPage />} />
              <Route path="/solutions/public-service-detail" element={<PublicServiceDetailPage />} />
              <Route path="/solutions/emergency-command-detail" element={<EmergencyCommandDetailPage />} />
              <Route path="/solutions/smart-park-detail" element={<SmartParkDetailPage />} />
              <Route path="/solutions/park-operation-detail" element={<ParkOperationDetailPage />} />
              <Route path="/solutions/park-intelligence-detail" element={<ParkIntelligenceDetailPage />} />
              <Route path="/solutions/smart-warehouse-detail" element={<SmartWarehouseDetailPage />} />
              <Route path="/solutions/wms-detail" element={<WMSDetailPage />} />
              <Route path="/solutions/logistics-distribution-detail" element={<LogisticsDistributionDetailPage />} />
              <Route path="/solutions/cold-chain-logistics-detail" element={<ColdChainLogisticsDetailPage />} />
              <Route path="/solutions/smart-finance-detail" element={<SmartFinanceDetailPage />} />
              <Route path="/solutions/digital-bank-detail" element={<DigitalBankDetailPage />} />
              <Route path="/solutions/smart-retail-detail" element={<SmartRetailDetailPage />} />
              <Route path="/solutions/e-commerce-detail" element={<ECommerceDetailPage />} />
              <Route path="/solutions/smart-energy-detail" element={<SmartEnergyDetailPage />} />
              <Route path="/solutions/smart-power-grid-detail" element={<SmartPowerGridDetailPage />} />
              <Route path="/solutions/smart-agriculture-detail" element={<SmartAgricultureDetailPage />} />
              <Route path="/solutions/smart-breeding-detail" element={<SmartBreedingDetailPage />} />
              <Route path="/solutions/livestock-management-detail" element={<LivestockManagementDetailPage />} />
              <Route path="/solutions/product-traceability-detail" element={<ProductTraceabilityDetailPage />} />
              <Route path="/solutions/smart-city-detail" element={<SmartCityDetailPage />} />
              <Route path="/solutions/smart-community-detail" element={<SmartCommunityDetailPage />} />
              <Route path="/solutions/smart-environment-detail" element={<SmartEnvironmentDetailPage />} />
              <Route path="/solutions/waste-management-detail" element={<WasteManagementDetailPage />} />
              <Route path="/solutions/smart-tourism-detail" element={<SmartTourismDetailPage />} />
              <Route path="/solutions/hotel-management-detail" element={<HotelManagementDetailPage />} />
              <Route path="/solutions/smart-hotel-detail" element={<SmartHotelDetailPage />} />
              <Route path="/solutions/restaurant-management-detail" element={<RestaurantManagementDetailPage />} />
              <Route path="/solutions/online-ordering-detail" element={<OnlineOrderingDetailPage />} />
              <Route path="/solutions/financial-information-detail" element={<FinancialInformationDetailPage />} />
              <Route path="/solutions/insurance-business-detail" element={<InsuranceBusinessDetailPage />} />
              <Route path="/solutions/internet-finance-detail" element={<InternetFinanceDetailPage />} />
              <Route path="/solutions/smart-media-detail" element={<SmartMediaDetailPage />} />
              <Route path="/solutions/new-media-detail" element={<NewMediaDetailPage />} />
              <Route path="/products" element={<Products />} />
              <Route path="/products/:id" element={<ProductDetail />} />
              <Route path="/service-support" element={<ServiceSupport />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/bind-phone" element={<PrivateRoute><BindPhone /></PrivateRoute>} />
              <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
              <Route path="/wechat-test" element={<WechatCallbackTest />} />
              <Route path="/wechat-confirm" element={<WechatConfirmLogin />} />
              <Route path="/admin" element={
                <AdminProtectedRoute>
                  <DashboardPage />
                </AdminProtectedRoute>
              } />
              <Route path="/admin/*" element={<AdminRoutes />} />
            </Routes>
            </Suspense>
          </MainContent>
          <Footer />
          <Suspense fallback={null}>
            <ChatWidget />
          </Suspense>
        </AppContainer>
        </Router>
      </HelmetProvider>
      </ErrorBoundary>
  );
}

export default App;